"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useMemo, useRef } from "react";
import { List, ChevronDown } from "lucide-react";
import { useT } from "@/lib/use-i18n";
// 2026-09-11 P1: slugify + dedup SSOT en simple-markdown (paridad ToC ↔
// HTML: mismos ids, unicode-aware, fallback por índice).
// 2026-09-28 RUN 6 FASE 3 BL-01: `extractHeadings` y `tocScrollMarginPx` viven
// en `./toc-scroll-margin` (módulo PURO, sin "use client") para que el layout de
// servidor calcule el offset `--toc-scroll-mt` sin arrastrar el grafo cliente.
//
// RUN 6 FASE 9: los re-exports de este fichero se BORRAN. La versión anterior
// los re-exportaba "para no romper el API público del componente", pero
// `audit:knip` demostró que NADA fuera de aquí los importaba: el Server
// Component (`BlogArticleLayout.tsx:19`) y el test
// (`__tests__/TableOfContents-scroll-margin.test.tsx:38`) importan del módulo
// puro directamente. Eran 6 exports muertos (5 valores + 1 tipo) y una
// superficie pública falsa que invitaba a importar el offset desde el cliente,
// que es justo lo que BL-01 quería evitar. Importar de aquí:
import { extractHeadings } from "./toc-scroll-margin";
interface TableOfContentsProps {
  body: string;
}
export function TableOfContents(t0) {
  const $ = _c(31);
  const {
    body
  } = t0;
  const t = useT();
  let t1;
  if ($[0] !== body) {
    t1 = extractHeadings(body);
    $[0] = body;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const headings = t1;
  const [activeId, setActiveId] = useState("");
  const observerRef = useRef(null);
  let t2;
  let t3;
  if ($[2] !== headings) {
    t2 = () => {
      if (headings.length < 2) {
        return;
      }
      const callback = entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        }
      };
      observerRef.current = new IntersectionObserver(callback, {
        rootMargin: "-80px 0px -70% 0px",
        threshold: 0
      });
      for (const heading of headings) {
        const el = document.getElementById(heading.id);
        if (el) {
          observerRef.current.observe(el);
        }
      }
      return () => observerRef.current?.disconnect();
    };
    t3 = [headings];
    $[2] = headings;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  if (headings.length < 2) {
    return null;
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("tocLabel");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const tocLabel = t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <List className="size-4 text-muted-foreground" aria-hidden="true" />;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== tocLabel) {
    t6 = <span className="flex items-center gap-2">{t5}{tocLabel}</span>;
    $[8] = tocLabel;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ChevronDown className="size-4 text-muted-foreground transition-transform group-open/toc:rotate-180" aria-hidden="true" />;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t6) {
    t8 = <summary className="flex w-full cursor-pointer list-none items-center justify-between gap-2 px-4 py-3 text-sm font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] sm:hidden [&::-webkit-details-marker]:hidden">{t6}{t7}</summary>;
    $[11] = t6;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== tocLabel) {
    t9 = <div className="hidden truncate px-0 pt-4 pb-2 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground sm:block" title={tocLabel}>{tocLabel}</div>;
    $[13] = tocLabel;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== activeId || $[16] !== headings) {
    let t11;
    if ($[18] !== activeId) {
      t11 = heading_0 => <li key={heading_0.id}><a href={`#${heading_0.id}`} onClick={_temp} aria-current={activeId === heading_0.id ? "location" : undefined} className={`block rounded-[var(--radius-xs)] px-2.5 py-1 text-sm transition-all border-s-2 border-transparent aria-[current=location]:border-[var(--accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2 ${heading_0.level === 3 ? "ps-4" : ""} ${activeId === heading_0.id ? "bg-[var(--accent)]/10 text-[var(--accent)] font-medium" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}>{heading_0.text}</a></li>;
      $[18] = activeId;
      $[19] = t11;
    } else {
      t11 = $[19];
    }
    t10 = headings.map(t11);
    $[15] = activeId;
    $[16] = headings;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[20] !== t10) {
    t11 = <ul className="space-y-1">{t10}</ul>;
    $[20] = t10;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11 || $[23] !== t9) {
    t12 = <div className="px-4 pb-4 sm:block">{t9}{t11}</div>;
    $[22] = t11;
    $[23] = t9;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12 || $[26] !== t8) {
    t13 = <details className="group/toc" open={true}>{t8}{t12}</details>;
    $[25] = t12;
    $[26] = t8;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t13 || $[29] !== tocLabel) {
    t14 = <nav aria-label={tocLabel} className="lovable-card rounded-[var(--radius-panel)] border border-border/80 shadow-xs lg:sticky lg:top-20 lg:max-h-[calc(100vh-10rem)] lg:max-h-[calc(100dvh-10rem)] lg:overflow-y-auto">{t13}</nav>;
    $[28] = t13;
    $[29] = tocLabel;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  return t14;
}
function _temp(e) {
  e.currentTarget.closest("details")?.removeAttribute("open");
}